import { tt } from '@agos/shared'

/**
 * Who is serving this table — `FRONTEND.md` §9.40, ported from legacy `new-order.tsx:341`.
 *
 * `buildOrderPayload` has carried `employeeId` since it was written and **nothing ever set it**, so
 * every table order AGOS took was written with no waiter against it. That is the person who owns the
 * money: the backend's `SalesChannel.requiresWaiter()` exists to ask for it, and the field it reads
 * was always null.
 */

export interface WaiterOption {
  id: number
  name: string
}

/** `GET /employees/waiters` — whoever is **on shift**, which is not the same as whoever is employed. */
export function waiterOptions(payload: unknown): WaiterOption[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as { id?: unknown; name?: unknown }
    if (typeof row.id !== 'number') return []
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    return [{ id: row.id, name: name === '' ? `#${row.id}` : name }]
  })
}

/**
 * Why this order cannot be saved yet, or `null`.
 *
 * Legacy's three refusals, kept apart because they are three different things to do about it:
 *
 * - **still loading** — wait, the answer is on its way;
 * - **nobody on shift** — somebody has to clock in before a table can be served, which is §9.32's
 *   widget and not something this screen can fix;
 * - **none chosen** — the list is there, pick from it.
 *
 * Collapsing them into *"vyberte čašníka"* would tell a waitress to choose from an empty list.
 */
export function waiterProblem(
  needsWaiter: boolean,
  loaded: boolean,
  waiters: readonly WaiterOption[],
  chosenId: number | null,
): string | null {
  if (!needsWaiter) return null
  if (!loaded) return tt('Zoznam čašníkov sa ešte načítava.')
  if (waiters.length === 0) {
    return tt('Žiadny čašník nie je nahlásený na smene. Nahláste aspoň jedného čašníka.')
  }
  if (chosenId === null) return tt('Pred uložením objednávky vyberte čašníka.')
  return null
}

/**
 * The waiter to start with: yourself, when you are one.
 *
 * The second half of the owner's request — *automaticky selektnut toho, kto objednáva*. A manager
 * taking an order for somebody else's table is offered nothing, because the app cannot know whose
 * table it is and guessing would put the takings against the wrong person.
 *
 * Only when they are actually on the shift list: a waiter who has not clocked in is not on it, and
 * pre-selecting an id the `<select>` has no option for would show an empty control that looks chosen.
 */
export function initialWaiterId(
  user: { id?: number; systemRole?: string } | null | undefined,
  waiters: readonly WaiterOption[],
): number | null {
  if (user?.systemRole !== 'waiter' || typeof user.id !== 'number') return null
  return waiters.some((waiter) => waiter.id === user.id) ? user.id : null
}

/**
 * The same question at the till — `FRONTEND.md` §9.128, the owner's point 13.
 *
 * Separate from `waiterProblem` only for its wording: the order is already saved by the time
 * somebody is paying, so *"pred uložením objednávky"* would name the wrong moment. The rules are the
 * same rules, and they are the reason legacy refuses the payment rather than defaulting: crediting
 * a colleague's takings to whoever happens to be at the screen is the very error being reported.
 */
export function payWaiterProblem(
  needsWaiter: boolean,
  loaded: boolean,
  waiters: readonly WaiterOption[],
  chosenId: number | null,
): string | null {
  if (!needsWaiter) return null
  if (!loaded) return tt('Zoznam čašníkov sa ešte načítava.')
  if (waiters.length === 0) {
    return tt('Žiadni čašníci s dochádzkou - pridajte dochádzku pred platbou.')
  }
  if (chosenId === null) return tt('Platbu nie je možné spracovať bez výberu čašníka.')
  return null
}

/**
 * Whether this kind of order is attributed to a waiter at all - §9.333.
 *
 * A delivery belongs to its driver, so it is never a waiter's.
 *
 * **This read `orderType === 'restaurant' || orderType === 'bar'`, and that missed `na_sekeru`** -
 * the owner's own channel, and the one they reported stuck: *"when using na _sekeru and its not
 * assigned to anyone, its in zoznam & archív as kontrola kvality, but we cant move it."* One of the
 * hardcoded channel lists §10A.1 exists to remove.
 *
 * **The server's rule is `usesTables`**, and `OrderStatusService.guardClosable` refuses to close an
 * order on such a channel without a waiter. So that is the rule here too, read off the channel.
 *
 * <p>The hardcoded pair stays as the fallback for a caller with no channel list yet - both
 * `restaurant` and `bar` have `usesTables`, so the two answers agree and a screen that has not
 * loaded the channels behaves exactly as it did.
 */
export function paymentNeedsWaiter(
  orderType: string | undefined,
  channels?: readonly { slug?: string; usesTables?: boolean }[],
): boolean {
  const known = channels?.find((channel) => channel.slug === orderType)
  if (known !== undefined) return known.usesTables === true
  return orderType === 'restaurant' || orderType === 'bar'
}

/**
 * Whether this order cannot be closed until somebody owns it - §9.333.
 *
 * The three conditions the server's own guard uses, in the same order: the order is still open, its
 * channel takes tables, and nobody is on it. All three, because a picker on a closed order or on one
 * that already has a waiter is a fourth place to set a waiter on a row where the question does not
 * arise.
 */
export function needsWaiterBeforeClosing(
  order: { status?: string; type?: string; employeeId?: number },
  channels?: readonly { slug?: string; usesTables?: boolean }[],
): boolean {
  if (order.status === 'delivered' || order.status === 'cancelled') return false
  if (!paymentNeedsWaiter(order.type, channels)) return false
  return order.employeeId === undefined || order.employeeId === null
}
